import React from "react";

import {
    Dropdown as UIKitDropdown,
    DropdownContent,
    DropdownSelect,
    DropdownSelectItem,
    Pill,
    Icon,
    type PillProps,
    type IconNameGeneric,
    type IconNameBrand,
    type IconProp,
} from "@proda-ai/ui-kit";
import { isTruthy } from "@/utils/guards";

import type { BoxProps } from "@/ui/atoms/Box";
import Box from "@/ui/atoms/Box";

export type PillDropdownOption<T> = {
    value: T;
    label: string;
    icon?: IconProp<IconNameGeneric, IconNameBrand>;
    disabled?: boolean;
    hidden?: boolean;
};

export type PillDropdownProps<T> = JSX.IntrinsicAttributes & {
    options: PillDropdownOption<T>[];
    value: T | null;
    onChange: (value: T) => void;
    placeholder?: string;
    disabled?: boolean;
    className?: string;
    id?: string;
    sx?: BoxProps["sx"];
    colour?: PillProps["textColor"];
};

export const testIds = {
    main: "PillDropdown",
    pill: "PillDropdown-pill",
};

const PillDropdown = <T extends string | number>(
    props: PillDropdownProps<T>,
) => {
    const {
        options,
        value,
        onChange,
        placeholder = "Select an option",
        disabled = false,
        className,
        id = "controlled-pill-dropdown",
        sx,
        colour = "proda-purple",
    } = props;

    const [isDropdownOpen, setIsDropdownOpen] = React.useState<boolean>(false);

    const selectedOption = options.find(option => option.value === value);

    const handleSelect = React.useCallback(
        (focusedIndex?: number): void => {
            if (focusedIndex === undefined) return;
            const selectedOption = options[focusedIndex];
            if (!selectedOption) return;
            onChange(selectedOption.value);
            setIsDropdownOpen(false);
        },
        [options, onChange],
    );

    return (
        <Box
            data-testid={testIds.main}
            className={className}
            sx={{
                width: "fit-content",
                "& .pill-dropdown-content": {
                    width: "max-content !important",
                    minWidth: "max-content !important",
                },
                ...sx,
            }}
        >
            <UIKitDropdown
                isShowing={isDropdownOpen}
                onClose={(): void => setIsDropdownOpen(false)}
                isDismissible={true}
                onItemSelected={handleSelect}
            >
                <Box
                    onClick={
                        disabled ? undefined : () => setIsDropdownOpen(true)
                    }
                    sx={{
                        cursor: disabled ? "not-allowed" : "pointer",
                        opacity: 1,
                    }}
                >
                    <Pill
                        data-testid={testIds.pill}
                        backgroundColor="proda-white"
                        textColor={colour}
                        borderColor={colour}
                    >
                        <Box
                            component="span"
                            sx={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                            }}
                        >
                            {selectedOption ? (
                                <>
                                    {selectedOption.icon !== undefined && (
                                        <Icon
                                            icon={selectedOption.icon}
                                            color={colour}
                                            size="sm"
                                        />
                                    )}
                                    <span>{selectedOption.label}</span>
                                </>
                            ) : (
                                <span>{placeholder}</span>
                            )}
                            {!disabled && (
                                <Icon
                                    icon="caret-down"
                                    color={colour}
                                    size="sm"
                                />
                            )}
                        </Box>
                    </Pill>
                </Box>
                <DropdownContent className="pill-dropdown-content">
                    <DropdownSelect
                        currentIndex={options.findIndex(
                            option => option.value === value,
                        )}
                        role="listbox"
                        labelledById={id}
                    >
                        {options.map(
                            option =>
                                !isTruthy(option.hidden) && (
                                    <DropdownSelectItem
                                        key={String(option.value)}
                                        role="option"
                                        onClick={(): void => {
                                            onChange(option.value);
                                            setIsDropdownOpen(false);
                                        }}
                                        isDisabled={option.disabled}
                                        label={
                                            <Box
                                                sx={{
                                                    display: "flex",
                                                    alignItems: "center",
                                                    gap: "8px",
                                                    whiteSpace: "nowrap",
                                                }}
                                            >
                                                {option.icon !== undefined && (
                                                    <Icon
                                                        icon={option.icon}
                                                        color={colour}
                                                        size="sm"
                                                    />
                                                )}
                                                <span>{option.label}</span>
                                            </Box>
                                        }
                                    />
                                ),
                        )}
                    </DropdownSelect>
                </DropdownContent>
            </UIKitDropdown>
        </Box>
    );
};

export default PillDropdown;
